import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Settings2 } from 'lucide-react';
import type { FormValues } from '@/lib/types';
import type { SopAnalysisResult } from '@/components/sop-intake/sop-analyzer/types';
import BodyDiagram, { DEFAULT_LATERALIZATION_MAP_SETTINGS, type LateralizationMapSettings as MapSettings } from '@/components/sop-intake/sop-analyzer/BodyDiagram';
import LateralizationDetailDrawer from '@/components/sop-intake/sop-analyzer/LateralizationDetailDrawer';
import LateralizationMapSettings from '@/components/sop-intake/sop-analyzer/LateralizationMapSettings';
import { cn } from '@/lib/utils';

export interface SopAnalysisPanelProps {
  analysis: SopAnalysisResult | null;
  isAnalyzing: boolean;
  completedTests: number;
  totalTests: number;
  formValues: FormValues;
  /** When true, the SoP Table supports Simple vs Detailed spectrum view; otherwise only Simple is available. */
  experimentalFeaturesEnabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  activeTab?: 'body-map' | 'sop-table';
  onTabChange?: (tab: 'body-map' | 'sop-table' | string) => void;
  visibleTestKeys?: ReadonlySet<string>;
}


const SopAnalysisPanel = ({
  analysis,
  completedTests,
  totalTests,
  isAnalyzing,
  formValues,
  experimentalFeaturesEnabled = false,
  open = false,
  onOpenChange,
  activeTab = 'body-map',
  onTabChange,
  visibleTestKeys,
}: SopAnalysisPanelProps) => {
  const [tableViewMode, setTableViewMode] = useState<'simple' | 'detailed'>('simple');
  const [mapSettings, setMapSettings] = useState<MapSettings>(DEFAULT_LATERALIZATION_MAP_SETTINGS);

  useEffect(() => {
    if (!experimentalFeaturesEnabled) {
      setTableViewMode('simple');
    }
  }, [experimentalFeaturesEnabled]);

  useEffect(() => {
    if (open) {
      setTableViewMode('simple');
    }
  }, [open]);

  const effectiveTableViewMode = experimentalFeaturesEnabled ? tableViewMode : 'simple';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onOpenChange?.(false)}>
      <DialogContent
        className="left-2 right-2 top-2 flex h-[calc(100dvh-4.5rem)] max-h-[calc(100dvh-4.5rem)] w-auto max-w-[calc(100vw-1rem)] translate-x-0 translate-y-0 flex-col overflow-hidden bg-background p-3 text-foreground border-border sm:left-[50%] sm:right-auto sm:top-1/2 sm:h-[95vh] sm:max-h-[95vh] sm:w-[95vw] sm:max-w-[95vw] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:p-4"
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>
            {activeTab === 'body-map' ? 'Lateralization Map (full screen)' : 'SoP Table (full screen)'}
          </DialogTitle>
        </VisuallyHidden>
        {!analysis ? (
          <div className="flex flex-1 items-center justify-center min-h-0">
            <p className="text-sm text-muted-foreground">Waiting for inputs…</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={(val) => onTabChange?.(val)} className="w-full flex flex-col flex-1 min-h-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/30 pb-3 shrink-0 pr-14">
              <div className="flex flex-col">
                <h2 className="text-lg font-semibold text-foreground leading-tight">{analysis.displaySummary}</h2>
                <p className="text-xs text-muted-foreground">{analysis.classificationCode}</p>
              </div>
              <div className="flex max-w-full flex-wrap items-center gap-2">
                {activeTab === 'sop-table' && experimentalFeaturesEnabled && (
                  <div className="inline-flex items-center rounded-md border border-border/70 bg-card/60 p-1">
                    <button
                      type="button"
                      onClick={() => setTableViewMode('simple')}
                      className={cn(
                        'rounded-sm px-3 py-1.5 text-xs font-medium transition-colors',
                        tableViewMode === 'simple'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-card'
                      )}
                    >
                      Simple
                    </button>
                    <button
                      type="button"
                      onClick={() => setTableViewMode('detailed')}
                      className={cn(
                        'rounded-sm px-3 py-1.5 text-xs font-medium transition-colors',
                        tableViewMode === 'detailed'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-card'
                      )}
                    >
                      Detailed
                    </button>
                  </div>
                )}
                <TabsList className="bg-card/50 text-muted-foreground h-9">
                  <TabsTrigger value="body-map" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs px-6">Body Map</TabsTrigger>
                  <TabsTrigger value="sop-table" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs px-6">SoP Table</TabsTrigger>
                </TabsList>
                {activeTab === 'body-map' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-card"
                        aria-label="Open map settings"
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-auto border-none p-0">
                      <LateralizationMapSettings settings={mapSettings} onChange={setMapSettings} />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            <TabsContent value="body-map" className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=active]:flex">
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl border border-border/30 bg-card/40 p-2 shadow-2xl sm:p-4">
                <BodyDiagram
                  analysis={analysis}
                  formValues={formValues}
                  settings={mapSettings}
                  visibleTestKeys={visibleTestKeys}
                />
              </div>
            </TabsContent>

            <TabsContent value="sop-table" className="mt-4 flex-1 flex-col min-h-0 data-[state=active]:flex overflow-y-auto pr-2">
              <LateralizationDetailDrawer
                analysis={analysis}
                formValues={formValues}
                viewMode={effectiveTableViewMode}
                visibleTestKeys={visibleTestKeys}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SopAnalysisPanel;
